import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Checkbox,
  Button,
  Input,
  message,
  Result,
  Spin,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  List,
  Collapse,
} from 'antd';
import {
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { loiAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f0f2f5',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans TC", "Microsoft JhengHei", sans-serif',
  },
  headerBanner: {
    background: 'linear-gradient(135deg, #001529 0%, #003a70 50%, #1890ff 100%)',
    padding: '40px 24px 36px',
    color: '#fff',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(ellipse at 20% 50%, rgba(24,144,255,0.15) 0%, transparent 60%),' +
      'radial-gradient(ellipse at 80% 50%, rgba(24,144,255,0.10) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 900,
    margin: '0 auto',
  },
  logoArea: {
    marginBottom: 20,
  },
  companyName: {
    fontSize: 32,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: 6,
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    marginTop: 6,
    letterSpacing: 2,
  },
  caseBadge: {
    display: 'inline-block',
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: 8,
    padding: '10px 28px',
    marginTop: 20,
  },
  caseNumber: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 2,
  },
  caseTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#fff',
  },
  body: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '32px 16px 48px',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 20,
    color: '#001529',
  },
  brandCard: {
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    border: 'none',
    height: '100%',
  },
  brandCardHead: {
    padding: '20px 24px 14px',
    borderBottom: '1px solid #f0f0f0',
  },
  brandLogo: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 18,
    color: '#fff',
    marginRight: 14,
    flexShrink: 0,
  },
  brandName: {
    fontSize: 20,
    fontWeight: 700,
    color: '#001529',
    margin: 0,
    lineHeight: 1.3,
  },
  tagRow: {
    marginTop: 12,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  productItem: {
    padding: '8px 0',
    fontSize: 14,
    borderBottom: '1px solid #f5f5f5',
  },
  interestSection: {
    background: '#fff',
    borderRadius: 12,
    padding: '32px 28px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
    marginTop: 32,
  },
  greeting: {
    fontSize: 18,
    fontWeight: 500,
    color: '#262626',
    marginBottom: 24,
  },
  checkboxItem: {
    display: 'block',
    marginBottom: 14,
    fontSize: 15,
    lineHeight: '28px',
  },
  submitBtn: {
    marginTop: 24,
    height: 48,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 8,
    width: '100%',
    maxWidth: 320,
  },
  footer: {
    background: '#001529',
    color: 'rgba(255,255,255,0.85)',
    padding: '36px 24px',
    marginTop: 48,
    textAlign: 'center',
  },
  contactCard: {
    display: 'inline-block',
    textAlign: 'left',
    margin: '0 20px 16px',
    verticalAlign: 'top',
  },
  contactName: {
    fontWeight: 600,
    fontSize: 15,
    color: '#fff',
    marginBottom: 4,
  },
  contactDetail: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.8,
  },
  footerSign: {
    marginTop: 20,
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: 2,
  },
};

const BRANDS_FALLBACK = [
  {
    id: 'comboware',
    name: 'Comboware',
    color: '#0050b3',
    group: '第7組 虛擬軟體',
    itemRange: '30~64',
    description:
      'Comboware 致力於提供企業級軟體定義基礎架構解決方案，涵蓋 AI 運算平台、超融合雲端、智能備份、GPU 運算、應用虛擬化及儲存虛擬化等全方位產品線。',
    products: [
      { name: 'ARCHEON AI INFRA Platform', desc: 'AI 基礎架構平台' },
      { name: 'ComboStack 超融合雲平台', desc: '軟體定義超融合架構' },
      { name: 'BackupStack 智能備份', desc: '企業級智能備份方案' },
      { name: 'GPUstack 智能雲平台', desc: 'GPU 加速運算平台' },
      { name: 'VDIStack 應用虛擬化', desc: '虛擬桌面與應用交付' },
      { name: 'ComboStor 儲存虛擬化', desc: '軟體定義儲存方案' },
    ],
    checkboxLabel: '我有興趣投標 Comboware 系列 (項次 30-64)',
  },
  {
    id: 'graphon',
    name: 'GraphOn',
    color: '#389e0d',
    group: '第7組 虛擬軟體',
    itemRange: '67, 80',
    description:
      'GraphOn Go-Global 提供高效能的雲端應用程式發佈解決方案，讓使用者透過瀏覽器即可存取企業應用程式，無需安裝任何用戶端軟體。',
    products: [
      { name: 'Go-Global 雲端應用系統', desc: '跨平台雲端應用發佈方案' },
    ],
    checkboxLabel: '我有興趣投標 GraphOn 系列 (項次 67, 80)',
  },
];

export default function LOISurveyPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!token) {
      setError('invalid_token');
      setLoading(false);
      return;
    }

    loiAPI
      .getSurveyData(token)
      .then((res) => {
        const data = res.data || res;
        if (data.already_submitted) {
          setAlreadySubmitted(true);
        }
        setSurveyData(data);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 410 || err?.response?.data?.already_submitted) {
          setAlreadySubmitted(true);
          setSurveyData(err?.response?.data || {});
        } else {
          setError(status === 404 ? 'not_found' : 'invalid_token');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const brands = surveyData?.brands?.length ? surveyData.brands : BRANDS_FALLBACK;
  const campaignName = surveyData?.campaign?.name || '113年第二次電腦軟體共同供應契約採購';
  const caseNumber = surveyData?.campaign?.case_number || 'LP5-113012';
  const dealerCompany = surveyData?.dealer?.company_name || '';
  const dealerContact = surveyData?.dealer?.contact_name || '';

  const handleCheckChange = (brandId, checked) => {
    setSelectedBrands((prev) =>
      checked ? [...prev, brandId] : prev.filter((id) => id !== brandId)
    );
  };

  const handleSubmit = async () => {
    if (selectedBrands.length === 0) {
      message.warning('請至少勾選一個有興趣的品牌，或填寫備註後再送出。');
      return;
    }

    setSubmitting(true);
    try {
      await loiAPI.submitSurvey(token, {
        interested_brand_ids: selectedBrands,
        notes: notes.trim(),
      });
      setSubmitted(true);
      message.success('感謝您的回覆！我們將盡快與您聯繫。');
    } catch (err) {
      const msg = err?.response?.data?.message || '送出失敗，請稍後再試。';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // -- Render: Loading --
  if (loading) {
    return (
      <div
        style={{
          ...styles.page,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  // -- Render: Error --
  if (error) {
    return (
      <div style={styles.page}>
        <div style={{ maxWidth: 600, margin: '80px auto', padding: '0 16px' }}>
          <Result
            status="error"
            title="無法載入問卷"
            subTitle={
              error === 'not_found'
                ? '此連結無效或已過期，請確認您的連結是否正確。'
                : '連結驗證失敗，請聯繫業務人員取得正確連結。'
            }
          />
        </div>
      </div>
    );
  }

  // -- Render: Already submitted --
  if (alreadySubmitted || submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.headerBanner}>
          <div style={styles.headerOverlay} />
          <div style={styles.headerContent}>
            <div style={styles.logoArea}>
              <div style={styles.companyName}>宇聯科技</div>
              <div style={styles.subtitle}>ULINK TECHNOLOGY</div>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 16px' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="已完成填寫"
            subTitle="感謝您的回覆！我們已收到您的意願調查，業務人員將盡快與您聯繫。"
          />
        </div>
        {renderFooter()}
      </div>
    );
  }

  // -- Render: Main survey --
  return (
    <div style={styles.page}>
      {/* Header Banner */}
      <div style={styles.headerBanner}>
        <div style={styles.headerOverlay} />
        <div style={styles.headerContent}>
          <div style={styles.logoArea}>
            <div style={styles.companyName}>宇聯科技</div>
            <div style={styles.subtitle}>ULINK TECHNOLOGY</div>
          </div>
          <div style={styles.caseBadge}>
            <div style={styles.caseNumber}>{caseNumber}</div>
            <div style={styles.caseTitle}>{campaignName}</div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {/* Product Showcase */}
        <Title level={4} style={styles.sectionTitle}>
          代理品牌與產品介紹
        </Title>

        <Row gutter={[24, 24]}>
          {brands.map((brand) => (
            <Col xs={24} md={12} key={brand.id}>
              <Card style={styles.brandCard} bodyStyle={{ padding: 0 }}>
                {/* Card Header */}
                <div style={styles.brandCardHead}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        ...styles.brandLogo,
                        background: `linear-gradient(135deg, ${brand.color || '#1890ff'}, ${brand.color || '#1890ff'}cc)`,
                      }}
                    >
                      {brand.name?.charAt(0) || 'B'}
                    </div>
                    <div>
                      <div style={styles.brandName}>{brand.name}</div>
                    </div>
                  </div>
                  <div style={styles.tagRow}>
                    <Tag color="blue">
                      組別: {brand.group || '第7組 虛擬軟體'}
                    </Tag>
                    <Tag color="geekblue">
                      項次: {brand.itemRange || brand.item_range || '-'}
                    </Tag>
                  </div>
                </div>

                {/* Card Body */}
                <div style={{ padding: '16px 24px 20px' }}>
                  <Paragraph
                    type="secondary"
                    style={{ fontSize: 13, marginBottom: 16 }}
                  >
                    {brand.description}
                  </Paragraph>

                  <Collapse
                    ghost
                    defaultActiveKey={brands.length <= 2 ? [brand.id] : []}
                    style={{ margin: '0 -12px' }}
                  >
                    <Panel
                      header={
                        <Text strong style={{ fontSize: 14 }}>
                          主要產品項目 ({brand.products?.length || 0})
                        </Text>
                      }
                      key={brand.id}
                    >
                      <List
                        size="small"
                        dataSource={brand.products || []}
                        renderItem={(item) => (
                          <div style={styles.productItem}>
                            <Text strong style={{ color: brand.color || '#1890ff' }}>
                              {item.name}
                            </Text>
                            {item.desc && (
                              <Text
                                type="secondary"
                                style={{ marginLeft: 8, fontSize: 13 }}
                              >
                                - {item.desc}
                              </Text>
                            )}
                          </div>
                        )}
                      />
                    </Panel>
                  </Collapse>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Interest Selection */}
        <div style={styles.interestSection}>
          <div style={styles.greeting}>
            {dealerCompany || dealerContact
              ? `${dealerCompany} ${dealerContact} 您好！`
              : '您好！'}
          </div>

          <Paragraph type="secondary" style={{ marginBottom: 24, fontSize: 14 }}>
            感謝您撥冗瀏覽，請勾選您有興趣投標的品牌，我們將提供更詳細的產品規格與報價資訊。
          </Paragraph>

          <div style={{ marginBottom: 24 }}>
            {brands.map((brand) => (
              <Checkbox
                key={brand.id}
                style={styles.checkboxItem}
                onChange={(e) => handleCheckChange(brand.id, e.target.checked)}
                checked={selectedBrands.includes(brand.id)}
              >
                <span style={{ fontSize: 15 }}>
                  {brand.checkboxLabel ||
                    `我有興趣投標 ${brand.name} 系列 (項次 ${brand.itemRange || brand.item_range || '-'})`}
                </span>
              </Checkbox>
            ))}
          </div>

          <Divider />

          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 14 }}>
              其他建議 / 備註
            </Text>
          </div>
          <TextArea
            rows={4}
            placeholder="如有任何問題或建議，歡迎在此留言..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ borderRadius: 8, marginBottom: 24 }}
          />

          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              style={styles.submitBtn}
              loading={submitting}
              onClick={handleSubmit}
            >
              送出意願調查
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      {renderFooter()}
    </div>
  );
}

function renderFooter() {
  return (
    <div style={styles.footer}>
      <Title
        level={5}
        style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 20 }}
      >
        聯絡窗口
      </Title>
      <div>
        <div style={styles.contactCard}>
          <div style={styles.contactName}>周伶瑋 (Jolie)</div>
          <div style={styles.contactDetail}>
            <PhoneOutlined style={{ marginRight: 6 }} />
            02-8773-0263 #181
          </div>
          <div style={styles.contactDetail}>
            <MailOutlined style={{ marginRight: 6 }} />
            jolie.chou@ulink.com.tw
          </div>
        </div>
        <div style={styles.contactCard}>
          <div style={styles.contactName}>蘇鈺涵 (Anny)</div>
          <div style={styles.contactDetail}>
            <PhoneOutlined style={{ marginRight: 6 }} />
            02-8773-0263 #182
          </div>
          <div style={styles.contactDetail}>
            <MailOutlined style={{ marginRight: 6 }} />
            anny.su@ulink.com.tw
          </div>
        </div>
      </div>
      <div style={styles.footerSign}>宇聯科技 敬上</div>
    </div>
  );
}
